import type { GmDailyStreakCardVariant } from '@/components/entities/gamification/GmDailyStreakCard';
import type { StreakXpAwardDto } from '@/queries/streak/streak.schema';

export const getStreakCardVariant = (
  day: number,
  currentStreak: number,
  isCountedToday: boolean,
): GmDailyStreakCardVariant['variant'] => {
  if (day <= currentStreak) {
    return 'completed';
  }

  if (!isCountedToday && day === currentStreak + 1) {
    return 'active';
  }

  return 'default';
};

export const expandStreakAwards = (awards: StreakXpAwardDto[], currentStreak: number) => awards.flatMap((award) => {
  // Open-ended awards cannot be expanded indefinitely; keep the next few days visible.
  const lastDay = award.dayTo ?? Math.max(award.dayFrom + 1, currentStreak + 3);

  return Array.from({ length: Math.max(0, lastDay - award.dayFrom + 1) }, (_, index) => ({
    day: award.dayFrom + index,
    xp: award.xpAmount,
    awardId: award.id,
  }));
});

export const getStreakFocusDays = (days: Array<{ day: number }>, currentStreak: number, isCountedToday: boolean) => {
  const completedDays = days.filter(({ day }) => day <= currentStreak);
  const upcomingDays = days.filter(({ day }) => day > currentStreak);
  const activeDay = isCountedToday ? undefined : upcomingDays.find(({ day }) => day === currentStreak + 1)?.day;

  return {
    activeDay,
    // The mobile viewport starts with two completed days, or three upcoming days after today's completion.
    mobileStartDay: isCountedToday
      ? upcomingDays[0]?.day ?? completedDays.at(-1)?.day
      : completedDays.at(-2)?.day ?? completedDays[0]?.day ?? activeDay ?? upcomingDays[0]?.day,
    lastCompletedDay: completedDays.at(-1)?.day,
    firstUpcomingDay: upcomingDays[0]?.day,
    completedCount: completedDays.length,
    upcomingCount: upcomingDays.length,
  };
};

export const isStreakFocusLoaded = (
  awards: StreakXpAwardDto[],
  currentStreak: number,
  isCountedToday: boolean,
  focus: ReturnType<typeof getStreakFocusDays>,
) => {
  const currentRangeLoaded = currentStreak === 0
    || awards.some(({ dayFrom, dayTo }) => dayFrom <= currentStreak && (dayTo === null || dayTo >= currentStreak));

  return currentRangeLoaded
    && focus.completedCount >= Math.min(2, currentStreak)
    && (isCountedToday
      ? focus.upcomingCount >= 3 && focus.firstUpcomingDay === currentStreak + 1
      : focus.activeDay === currentStreak + 1);
};
